import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { createComment, fetchComments } from '@sola/my-sdk/content-feed/comments.ts';
import { contentFeedCommentMaxLength } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { ContentFeedCommentResolved as CommentResolved } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import { useSupporterDetails } from '@sola/supporter-sdk/hooks/details.ts';
import { deriveInitialsFromName, getSupporterFullName, getSupporterPreferredName } from '@sola/supporter-sdk/utils/name-format.ts';
import { trackContentFeedComment, trackError } from '@sola/tracking/components/datalayer.tsx';

/**
 * Re-exported from the schema lib so consumers of this molecule (and the
 * organism above it) keep a stable local `CommentResolved` name.
 */
export type { ContentFeedCommentResolved as CommentResolved } from '@sola/schema/dotcom/proxy-api/content-feed.ts';

/**
 * The "top N reactions" rule is shared with the reactions molecule. Re-exported
 * here so comment components keep a single import site, while there's only one
 * implementation (and one test) in `content-feed-reactions.logic.ts`.
 */
export { getTopReactions } from '../content-feed-reactions/content-feed-reactions.logic.ts';

/** Number of comments shown when the list is collapsed. */
export const collapsedCommentCount = 1;

/**
 * Nullable-safe initials derivation: returns `undefined` when there's no name
 * so the avatar can fall back to its icon.
 */
export const getInitials = (name?: string | null): string | undefined => name ? deriveInitialsFromName(name) || undefined : undefined;

/** Comparator ordering comments oldest-first by `publishedAt`. */
/**
 * Selects which comments to render, preserving the order the API returns them
 * in (the Content API is the source of truth for comment ordering). When
 * collapsed, only the first comment is shown; when expanded, all are. Does not
 * mutate the input.
 */
export const getVisibleComments = (comments: CommentResolved[], isListExpanded: boolean): CommentResolved[] => isListExpanded ? comments : comments.slice(0, collapsedCommentCount);

/**
 * Whether the "See all" link should be shown: there are more comments on the
 * entry than are currently rendered.
 */
export const shouldShowSeeAll = (commentCount: number, loadedCommentCount: number, isListExpanded: boolean): boolean => {
  const rendered = isListExpanded ? loadedCommentCount : collapsedCommentCount;
  return commentCount > rendered;
};

/**
 * Whether the whole section has nothing to render: commenting may be enabled,
 * but the input is hidden, the list isn't expanded, and there are no comments.
 */
export const hasNothingToRender = (params: {
  isInputVisible: boolean;
  isListExpanded: boolean;
  commentCount: number;
}): boolean => !params.isInputVisible && !params.isListExpanded && params.commentCount === 0;

/**
 * Represents the comment input's current intent.
 * - 'idle': input hidden
 * - 'comment': generic comment mode
 * - { reply: string }: replying to a specific comment by id
 */
export type CommentInputMode = 'idle' | 'comment' | {
  reply: string;
};
export interface UseContentFeedCommentsProps {
  entryId: string;
  /** Initial top comment from the entry response */
  topComment?: CommentResolved | null;
  /** Total number of comments on this entry */
  commentCount?: number;
  /** Whether commenting is enabled for this entry */
  canComment?: boolean;
  /** Title of the parent entry — sent as `content_title` in the comment event */
  contentTitle?: string;
  /** Landing-page URL of the parent entry — sent as `content_url` in the comment event */
  contentUrl?: string;
}
export const useContentFeedComments = ({
  entryId,
  contentTitle,
  contentUrl,
  topComment: initialTopComment = null,
  commentCount: initialCommentCount = 0,
  canComment = true
}: UseContentFeedCommentsProps) => {
  const {
    t
  } = useTranslation(['my']);
  const {
    authUser
  } = useAuth('authUser');
  const {
    data: supporterDetails
  } = useSupporterDetails();
  const [comments, setComments] = useState<CommentResolved[]>(initialTopComment ? [initialTopComment] : []);
  const [commentCount, setCommentCount] = useState(initialCommentCount);
  const [inputText, setInputText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputMode, setInputMode] = useState<CommentInputMode>('idle');
  const replyToId = typeof inputMode === 'object' ? inputMode.reply : null;
  const charCount = inputText.length;
  const isOverLimit = charCount > contentFeedCommentMaxLength;
  const canSubmit = inputText.trim().length > 0 && !isOverLimit && !isSubmitting;

  /** Toggle the comment input: if showing for any reason, close it; otherwise open in comment mode. */
  const toggleComment = () => {
    setInputMode(prev => prev === 'idle' ? 'comment' : 'idle');
  };

  /** Open the input in reply mode for a specific comment. */
  const openReply = (commentId: string) => {
    // Prevent replying to optimistic comments that haven't been persisted yet
    if (commentId.startsWith('pending-')) {
      return;
    }
    setInputMode({
      reply: commentId
    });
  };

  /** Cancel reply context — fall back to generic comment mode. */
  const cancelReply = () => {
    setInputMode('comment');
  };
  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    const text = inputText.trim();

    // Optimistic: add the comment to the UI immediately
    const optimisticComment: CommentResolved = {
      id: `pending-${Date.now()}`,
      text,
      author: {
        id: authUser?.authUserGlobalId || 'self',
        displayName: getSupporterPreferredName(supporterDetails) || getSupporterFullName(supporterDetails) || authUser?.supporter?.preferredName || authUser?.supporter?.firstName || '',
        photo: null
      },
      status: 'pendingModeration',
      publishedAt: new Date().toISOString(),
      children: [],
      reactions: []
    };
    if (replyToId) {
      // Optimistic: insert reply into the parent comment's children
      setComments(prev_0 => prev_0.map(c => c.id === replyToId ? {
        ...c,
        children: [...(c.children || []), optimisticComment]
      } : c));
    } else {
      setComments(prev_1 => [...prev_1, optimisticComment]);
      setCommentCount(prev_2 => prev_2 + 1);
    }
    setInputText('');
    try {
      await createComment(replyToId ? undefined : entryId, replyToId || undefined, text);
      trackContentFeedComment({
        contentId: entryId,
        contentTitle: contentTitle || '',
        contentUrl,
        commentId: replyToId || undefined
      });
    } catch {
      // Rollback optimistic update on failure
      if (replyToId) {
        setComments(prev_3 => prev_3.map(c_0 => c_0.id === replyToId ? {
          ...c_0,
          children: (c_0.children || []).filter(ch => ch.id !== optimisticComment.id)
        } : c_0));
      } else {
        setComments(prev_4 => prev_4.filter(c_1 => c_1.id !== optimisticComment.id));
        setCommentCount(prev_5 => prev_5 - 1);
      }
      trackError('content_feed_comment_create_failed');
    } finally {
      setIsSubmitting(false);
      setInputMode('idle');
    }
  };
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [hasLoadedAll, setHasLoadedAll] = useState(false);
  const handleLoadAllComments = async () => {
    if (isLoadingAll || hasLoadedAll) {
      return;
    }
    setIsLoadingAll(true);
    try {
      const response = await fetchComments({
        entryId,
        limit: 100
      });
      setComments(response.comments as CommentResolved[]);
      setHasLoadedAll(true);
    } catch {
      trackError('content_feed_comments_load_all_failed');
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Resolve the display name of the comment author being replied to
  const replyToName = replyToId ? (() => {
    for (const c_2 of comments) {
      if (c_2.id === replyToId) {
        return c_2.author.displayName;
      }
      if (c_2.children) {
        const child = c_2.children.find(ch_0 => ch_0.id === replyToId);
        if (child) {
          return child.author.displayName;
        }
      }
    }
    return;
  })() : undefined;
  return {
    // Parent entry context — forwarded to comment-level reactions so a like on
    // a comment can report the post it belongs to (content_id/title/url) while
    // carrying the comment id as comment_id.
    entryId,
    contentTitle,
    contentUrl,
    comments,
    commentCount,
    inputText,
    setInputText,
    charCount,
    maxLength: contentFeedCommentMaxLength,
    isOverLimit,
    canSubmit,
    canComment,
    isSubmitting,
    inputMode,
    replyToId,
    replyToName,
    handleSubmit,
    toggleComment,
    openReply,
    cancelReply,
    handleLoadAllComments,
    isLoadingAll,
    hasLoadedAll,
    t
  };
};